import React, { useState, useRef, useCallback } from 'react';
import { warehouseAPI } from '../services/api';
import { getCartonStatusBadge } from '../utils/helpers';
import toast from 'react-hot-toast';

// ── Inline styles ─────────────────────────────────────────────────────────────
const card = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const sectionTitle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 14,
};

const pill = (color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 99,
  fontSize: 12,
  fontWeight: 500,
  background: color + '22',
  color: color,
  border: `1px solid ${color}44`,
});

// ── PhotoCapture modal ────────────────────────────────────────────────────────
function PhotoCaptureModal({ cartonId, onClose, onUploaded }) {
  const [mode, setMode] = useState('choose'); // 'choose' | 'camera' | 'preview'
  const [photoType, setPhotoType] = useState('RECEIVING');
  const [caption, setCaption] = useState('');
  const [capturedImage, setCapturedImage] = useState(null); // base64
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setMode('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera permission or use file upload.');
      setMode('choose');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const base64 = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(base64);
    stopCamera();
    setMode('preview');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCapturedImage(reader.result);
      setMode('preview');
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!capturedImage) return;
    setLoading(true);
    try {
      await warehouseAPI.uploadPhoto(cartonId, {
        photoData: capturedImage,
        photoType,
        caption,
      });
      toast.success('Photo uploaded!');
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const retake = () => {
    setCapturedImage(null);
    setMode('choose');
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal"
        style={{ maxWidth: 500 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">📷 Add Photo Proof</h2>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}
          >
            ✕
          </button>
        </div>

        {/* Photo Type + Caption always visible */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Photo Type</label>
            <select
              className="form-select"
              value={photoType}
              onChange={(e) => setPhotoType(e.target.value)}
            >
              <option value="RECEIVING">📦 Receiving</option>
              <option value="DAMAGE">⚠️ Damage</option>
              <option value="DELIVERY">✅ Delivery</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Caption (optional)</label>
            <input
              className="form-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Front side of box"
            />
          </div>
        </div>

        {/* ── Mode: choose ── */}
        {mode === 'choose' && (
          <div>
            {cameraError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 12, fontSize: 13, color: '#ef4444', marginBottom: 14 }}>
                ⚠️ {cameraError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center', fontSize: 15, padding: '14px 0' }}
                onClick={startCamera}
              >
                📷 Open Camera
              </button>
              <button
                className="btn btn-secondary"
                style={{ flex: 1, justifyContent: 'center', fontSize: 15, padding: '14px 0' }}
                onClick={() => fileInputRef.current?.click()}
              >
                🖼️ Upload File
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        )}

        {/* ── Mode: camera ── */}
        {mode === 'camera' && (
          <div>
            <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#000', marginBottom: 12 }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'cover' }}
              />
              <div style={{ position: 'absolute', inset: 0, border: '3px solid rgba(99,102,241,0.6)', borderRadius: 10, pointerEvents: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => { stopCamera(); setMode('choose'); }} style={{ flex: 1, justifyContent: 'center' }}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={capturePhoto}
                style={{ flex: 2, justifyContent: 'center', fontSize: 15, padding: '12px 0' }}
              >
                📸 Capture
              </button>
            </div>
          </div>
        )}

        {/* ── Mode: preview ── */}
        {mode === 'preview' && capturedImage && (
          <div>
            <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12, border: '2px solid var(--border)' }}>
              <img
                src={capturedImage}
                alt="Preview"
                style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'contain', background: '#000' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={retake} style={{ flex: 1, justifyContent: 'center' }}>
                🔄 Retake
              </button>
              <button
                className="btn btn-success"
                onClick={handleUpload}
                disabled={loading}
                style={{ flex: 2, justifyContent: 'center', fontSize: 15 }}
              >
                {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '✅ Save Photo'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PhotoGallery ──────────────────────────────────────────────────────────────
function PhotoGallery({ photos, onDelete }) {
  const [lightbox, setLightbox] = useState(null);

  if (!photos || photos.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
        No photos yet. Add photos as proof of receipt.
      </div>
    );
  }

  const typeColors = {
    RECEIVING: '#6366f1',
    DAMAGE: '#f59e0b',
    DELIVERY: '#10b981',
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
        {photos.map((photo) => (
          <div key={photo.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', aspectRatio: '1' }}
            onClick={() => setLightbox(photo)}
          >
            <img
              src={photo.photoUrl}
              alt={photo.photoType}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={{ position: 'absolute', top: 4, left: 4 }}>
              <span style={pill(typeColors[photo.photoType] || '#6366f1')}>
                {photo.photoType === 'RECEIVING' ? '📦' : photo.photoType === 'DAMAGE' ? '⚠️' : '✅'}
                {' '}{photo.photoType}
              </span>
            </div>
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 99, color: '#fff', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox.photoUrl}
            alt={lightbox.photoType}
            style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
          />
          <button
            style={{ position: 'fixed', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 99, color: '#fff', width: 40, height: 40, cursor: 'pointer', fontSize: 20 }}
            onClick={() => setLightbox(null)}
          >✕</button>
        </div>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BdReceive() {
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanData, setScanData] = useState(null);   // BdScanResponse
  const [photos, setPhotos] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const inputRef = useRef(null);

  const handleScan = async (e) => {
    e?.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;
    setScanning(true);
    setScanData(null);
    setPhotos([]);
    try {
      const res = await warehouseAPI.bdScan(code);
      const data = res.data.data;
      setScanData(data);
      setPhotos(data.photos || []);
      toast.success(`Carton received! ${data.totalProductTypes || 0} product type(s) found.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Barcode not found');
      setScanData(null);
    } finally {
      setScanning(false);
    }
  };

  const reloadPhotos = async () => {
    if (!scanData?.cartonId) return;
    try {
      const res = await warehouseAPI.getPhotos(scanData.cartonId);
      setPhotos(res.data.data || []);
    } catch { /* ignore */ }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await warehouseAPI.deletePhoto(photoId);
      toast.success('Photo deleted');
      reloadPhotos();
    } catch (err) {
      toast.error('Failed to delete photo');
    }
  };

  const statusColor = {
    IN_BD_WAREHOUSE: '#10b981',
    DELIVERED: '#6366f1',
    ARRIVED_BD: '#f59e0b',
    IN_TRANSIT: '#3b82f6',
    DEPARTED: '#8b5cf6',
  };

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">🇧🇩 BD Receiving</h1>
        <p className="page-subtitle">Scan carton barcode to receive goods and add photo proof</p>
      </div>

      {/* ── Scan Input ── */}
      <div style={card}>
        <div style={sectionTitle}>Scan Barcode / QR Code</div>
        <form onSubmit={handleScan} style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label className="form-label">Barcode Value</label>
            <input
              ref={inputRef}
              className="form-input"
              style={{ fontSize: 16, letterSpacing: '0.04em', fontFamily: 'var(--font-mono)' }}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              placeholder="CGO-1-1234567890  —  scan or type manually"
              autoFocus
              autoComplete="off"
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
              Point your barcode scanner at the carton sticker, or type the code manually.
            </span>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={scanning || !barcodeInput.trim()}
            style={{ padding: '10px 28px', fontSize: 15, height: 42 }}
          >
            {scanning
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Scanning…</>
              : '⟳ Receive'}
          </button>
        </form>
      </div>

      {/* ── Scan Result ── */}
      {scanData && (
        <>
          {/* Carton Summary */}
          <div style={{ ...card, borderLeft: `4px solid ${statusColor[scanData.status] || '#6366f1'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={sectionTitle}>📦 Carton Details</div>
              <span className={`badge ${getCartonStatusBadge(scanData.status)}`}>{scanData.status}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { label: 'GRN Number',       value: scanData.grnNumber,              mono: true },
                { label: 'Barcode',          value: scanData.barcode,                mono: true },
                { label: 'Customer ID',      value: scanData.customerId },
                { label: 'Shipping Mark',    value: scanData.shippingMark },
                { label: 'Supplier Tracking',value: scanData.supplierTrackingNumber },
                { label: 'Weight',           value: scanData.weightKg ? `${scanData.weightKg} kg` : null },
                { label: 'CBM',              value: scanData.cbm },
                { label: 'Description',      value: scanData.productDescription, wide: true },
                { label: 'Notes',            value: scanData.notes, wide: true },
              ].map(({ label, value, mono, wide }) =>
                value ? (
                  <div key={label} style={wide ? { gridColumn: '1/-1' } : {}}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : undefined }}>{value}</div>
                  </div>
                ) : null
              )}
            </div>
          </div>

          {/* Products List */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={sectionTitle}>🗂 Products Inside This Carton</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={pill('#6366f1')}>{scanData.totalProductTypes} type{scanData.totalProductTypes !== 1 ? 's' : ''}</span>
                <span style={pill('#10b981')}>{scanData.totalItems} total pcs</span>
              </div>
            </div>

            {!scanData.products || scanData.products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                No products packed into this carton yet.
              </div>
            ) : (
              <div className="table-wrapper" style={{ margin: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product Code</th>
                      <th>Product Name</th>
                      <th>SKU</th>
                      <th>Quantity</th>
                      <th>Unit</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanData.products.map((p, idx) => (
                      <tr key={p.id}>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                        <td>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>
                            {p.productCode}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{p.productName}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.sku || '—'}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15 }}>
                            {p.quantity}
                          </span>
                        </td>
                        <td>{p.unit || 'pcs'}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Photos */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={sectionTitle}>📸 Photo Proof ({photos.length})</div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowPhotoModal(true)}
              >
                + Add Photo
              </button>
            </div>
            <PhotoGallery photos={photos} onDelete={handleDeletePhoto} />
          </div>
        </>
      )}

      {/* Empty state when no scan yet */}
      {!scanData && !scanning && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📦</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Ready to Receive
          </div>
          <div style={{ fontSize: 14 }}>
            Scan a carton barcode above to see all products inside and add photo proof.
          </div>
        </div>
      )}

      {/* Photo Capture Modal */}
      {showPhotoModal && scanData && (
        <PhotoCaptureModal
          cartonId={scanData.cartonId}
          onClose={() => setShowPhotoModal(false)}
          onUploaded={reloadPhotos}
        />
      )}
    </div>
  );
}
